import { Deserializer } from "jsonapi-serializer";
import { ExpiryReason } from "../models/ExpiryReason";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { ExpiryReasonWrapper } from "../models/wrappers/ExpiryReasonWrapper";
import { APIConnector } from './api/APIConnector';
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "@/constants/response-status-codes";
import { isSuccessStatus } from "@/utilities/requestResponseValidator";
import { DeleteEntityResult } from "@/models/services/DeleteEntityResult";
import { EXPIRY_UNIT_TYPE } from "@/constants/expiry-reason-constant";
import { convertDurationByUnits } from "@/utilities/DateUtils";
import { MasterExpiry } from "@/models/MasterExpiry";
import { Name } from "@/models/Name";
import { PaginationModel } from "@/models/Pagination";
const apiConnector = new APIConnector();

export const getExpiryReasons = async (listParameters?: ListQuery): Promise<ExpiryReasonWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `expiryReason${partialURL}`.replace('&', '?'); //HACK: I am not sure if this is a better way of doing this.

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const rows: ExpiryReason[] = data.map((expiryReason): ExpiryReason => {
                    return {
                        id: expiryReason['id'],
                        name: expiryReason['er-name'],
                        nameOriginal: expiryReason['er-name'],
                        duration: convertDurationByUnits(expiryReason['er-duration'], expiryReason['er-units']).toString(),
                        durationWarning: convertDurationByUnits(expiryReason['er-duration-warn'], EXPIRY_UNIT_TYPE.DAYS).toString(),
                        durationValue: expiryReason['er-duration'],
                        durationUnit: expiryReason['er-units'],
                        durationWarnValue: expiryReason['er-duration-warn'],
                        isDefault: expiryReason['is-default'],
                        eventExpiry: expiryReason['event-expiry'],
                        eventWarning: expiryReason['event-warning'],
                        totalMasterExpiries: expiryReason['total-master-expiries']
                    }
                })

                return resolve({ rows, pagination: pagination });
            })
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getMasterExpiryForPerson = async (masterId: string, listParameters?: ListQuery): Promise<any> => {
    try {
        const masterFilters = `master.id==${masterId}`
        let filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filters = filters ? `${filters};${masterFilters}` : `&filter=${masterFilters}`;
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const masterExpiryFields = 'fields[masterExpiry]=expiryReason,meStartDateTime,meEndDateTime';
        const expiryReasonFields = 'fields[expiryReason]=erName,erUnits,erDuration,erDurationWarn,erNo';
        const url = `masterExpiry?include=expiryReason${partialURL}&${masterExpiryFields}&${expiryReasonFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: any[] = data.map((masterExpiry): any => ({
                    id: masterExpiry['id'],
                    expiryReasonId: masterExpiry['expiry-reason']['id'],
                    name: masterExpiry['expiry-reason']['er-name'],
                    duration: convertDurationByUnits(masterExpiry['expiry-reason']['er-duration'], masterExpiry['expiry-reason']['er-units']).toString(),
                    durationWarning: convertDurationByUnits(masterExpiry['expiry-reason']['er-duration-warn'], EXPIRY_UNIT_TYPE.DAYS).toString(),
                    masterExpiryStartDate: masterExpiry['me-start-date-time']?.split("T")[0],
                    masterExpiryEndDate: masterExpiry['me-end-date-time']?.split("T")[0],
                }));

                return resolve({ rows: rows, pagination });
            })
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getExpiryReasonNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const expiryReasonFields = "fields[expiryReason]=erName";
        const url = `expiryReason?${paginationString}&${expiryReasonFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const expiryReasons: Name[] = data.map((expiryReason)=> {
                    return {
                        value: expiryReason['id'],
                        label: expiryReason['er-name']
                    }
                });

                return resolve({ rows: expiryReasons, pagination });
            })
        })

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
} 

export const getMasterExpiryById = async (masterExpiryId: string): Promise<MasterExpiry> => {
    try {
        const masterExpiryFields = 'fields[masterExpiry]=expiryReason,meStartDateTime,meEndDateTime,master';
        const expiryReasonFields = 'fields[expiryReason]=erName,erUnits,erDuration,erDurationWarn';
        const masterFields = 'fields[master]=mstDisplayname';
        const url = `masterExpiry/${masterExpiryId}?include=expiryReason,master&${masterExpiryFields}&${expiryReasonFields}&${masterFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const masterExpiry: MasterExpiry = {
                    id: data['id'],
                    meStartDateTime: data['me-start-date-time'],
                    meEndDateTime: data['me-end-date-time'],
                    expiryReason: {
                        id: data['expiry-reason']['id'],
                        name: data['expiry-reason']['er-name'],
                        nameOriginal: data['expiry-reason']['er-name'],
                        duration: convertDurationByUnits(data['expiry-reason']['er-duration'], data['expiry-reason']['er-units']).toString(),
                        durationWarning: convertDurationByUnits(data['expiry-reason']['er-duration-warn'], EXPIRY_UNIT_TYPE.DAYS).toString(),
                        durationValue: data['expiry-reason']['er-duration'],
                        durationUnit: data['expiry-reason']['er-units'],
                        durationWarnValue: data['expiry-reason']['er-duration-warn'],
                        eventExpiry: data['event-expiry'],
                        eventWarning: data['event-warning']
                    },
                    master: {
                        id: data['master']['id'],
                        name: data['master']['mst-displayname'],
                        ...data['master'] // We have populate those that we are interested in, we just fake the rest of the fields
                    }
                };
                
                resolve(masterExpiry);
            });
        });

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getExpiryReasonById = async (expiryReasonId: string): Promise<ExpiryReason> => {
    try {
        const expiryReasonFields = "fields[expiryReason]=erName,erUnits,erDuration,erDurationWarn,erNo,totalMasterExpiries";
        const url = `expiryReason/${expiryReasonId}?${expiryReasonFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const expiryReason: ExpiryReason = {
                    id: data['id'],
                    name: data['er-name'],
                    nameOriginal: data['er-name'],
                    duration: convertDurationByUnits(data['er-duration'], data['er-units']).toString(),
                    durationWarning: convertDurationByUnits(data['er-duration-warn'], EXPIRY_UNIT_TYPE.DAYS).toString(),
                    durationValue: data['er-duration'],
                    durationUnit: String(data['er-units']),
                    durationWarnValue: data['er-duration-warn'],
                    isDefault: data['is-default'],
                    eventExpiry: data['event-expiry'],
                    eventWarning: data['event-warning'],
                    totalMasterExpiries: data['total-master-expiries']
                };

                resolve(expiryReason);
            });
        })

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const saveExpiryReason = async (expiryReason: ExpiryReason, createNew = true): Promise<any> => {
    try {
        const body = {
            data: {
                type: 'expiryReason',
                id: expiryReason.id,
                attributes: {
                    erName: expiryReason.name,
                    erDuration: Number(expiryReason.durationValue),
                    erUnits: Number(expiryReason.durationUnit),
                    erDurationWarn: Number(expiryReason.durationWarnValue ?? 0),
                }
            }
        };

        const url = `expiryReason${createNew ? '' : `/${expiryReason.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createNew, body).then((res) => {
            response = res;
        }).catch((error) => {
            return Promise.reject(processServerError(error));
        });

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (createNew) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({
                            status: response.status,
                            message: getConstraintViolation(content.errors[0].detail)
                        });
                    }
                    new Deserializer().deserialize(content, (error, data) => {
                        if (error) {
                            return reject({ status: response.status, success: false, message: genericError });
                        }
                        return resolve({ id: data.id, success: true });
                    });
                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: expiryReason.id, success: true });
                }

                const content = await response.json();
                return reject({
                    status: response.status,
                    success: false,
                    message: getConstraintViolation(content.errors[0].detail)
                });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, success: false, message: genericError });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const assignUnassignMasterExpiry = async (toBeAssigned: ExpiryReason[], toBeUnassigned: string[], masterId: string, values: any) => {
    const toBeAdded = toBeAssigned.map((reason) => {
        const startDate = values.useSameDate ? values.startDate : values[`startDate_${reason.id}`];
        const newMasterExpiry = buildToBeSavedMasterExpiry(reason, masterId, startDate);
        return saveMasterExpiry(newMasterExpiry);
    });

    const toBeRemoved = toBeUnassigned.map((expiryId) => deleteMasterExpiry(expiryId));

    return Promise.all([...toBeAdded, ...toBeRemoved]);
};

export const assignUnassignMasterExpiryFromExpiryReason = async ( masterIdsToAssign: string[],  masterExpiryIdsToDelete: string[], expiryReason: ExpiryReason, values: any) => {
    const toBeAdded = masterIdsToAssign.map((masterId) => {
        const startDate = values.startDate
        const payload = buildToBeSavedMasterExpiry(expiryReason, masterId, startDate);
        return saveMasterExpiry(payload);
    });

    const toBeRemoved = masterExpiryIdsToDelete.map((id) => deleteMasterExpiry(id));

    return Promise.all([...toBeAdded, ...toBeRemoved]);
};

const buildToBeSavedMasterExpiry = (expiryReason: ExpiryReason, masterId: string, startDate: string) => {
    const toISO = (date: Date) => date.toISOString().split("T")[0];

    return {
        data: {
            type: "masterExpiry",
            attributes: {
                meStartDateTime: toISO(new Date(startDate)),
            },
            relationships: {
                expiryReason: {
                    data: {
                        type: "expiryReason",
                        id: expiryReason.id
                    }
                },
                master: {
                    data: {
                        type: "master",
                        id: masterId
                    }
                },
            },
        },
    };
};

const saveMasterExpiry = async (masterExpiry: any, masterExpiryId?: string) => {
    try {
        let url = "masterExpiry";
        let response: Response;
        
        if (masterExpiryId) {
            url = `${url}/${masterExpiryId}`;
            masterExpiry.data.id = masterExpiryId;
        }
        
        await apiConnector.sendData(url, masterExpiryId ? false : true, masterExpiry).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            });
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve(true);
            }
            const json = await response.json();
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const editMasterExpiry = async (masterExpiry: MasterExpiry): Promise<any> => {
    try {
        const masterExpiryPayload = buildToBeSavedMasterExpiry(masterExpiry.expiryReason, masterExpiry.master.id.toString(), masterExpiry.meStartDateTime);
        return saveMasterExpiry(masterExpiryPayload, masterExpiry.id.toString());
    } 
    catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const deleteExpiryReason = async (expiryReasonId: string): Promise<DeleteEntityResult> => {
    try {
        const url = `expiryReason/${expiryReasonId}`;
        const expiryReason = {
            data: {
                type: 'expiryReason',
                id: expiryReasonId
            }
        }

        const response = await apiConnector.deleteData(url, expiryReason);

        return new Promise(async (resolve, reject) => {
            if (isSuccessStatus(response.status)) {
                return resolve({ id: expiryReasonId, success: true });
            }
            const json = await response.json();
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const deleteMasterExpiry = async (masterExpiryId: string): Promise<DeleteEntityResult> => {
    try {
        const url = `masterExpiry/${masterExpiryId}`;
        const body = {
            data: {
                type: 'masterExpiry',
                id: masterExpiryId
            }
        };
        let response: Response;

        await apiConnector.deleteData(url, body).then(async (data) => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error));
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve({ id: masterExpiryId, success: true });
            }
            const json = await response.json();
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}